import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { UnderlineTabs, type UnderlineTabOption } from "@/components/ui/underline-tabs";
import { CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { SigningFilterControl } from "./SigningFilterControl";
import type { SigningPageState, SigningTab } from "../useSigningPageState";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface SigningPageHeaderProps {
  state: SigningPageState;
}

/** Page title, the toolbar and the tab strip. */
export function SigningPageHeader({ state }: SigningPageHeaderProps) {
  const { t } = useLanguage();

  const tabs: Array<UnderlineTabOption<SigningTab>> = [
    { value: "mine", label: t.signing.tabs.mine },
    { value: "all", label: t.signing.tabs.all },
  ];

  return (
    <div className="border-b px-7 pt-4">
      <div className="mb-3">
        <PageTitle info="signing">{t.signing.title}</PageTitle>
        <PageSubtitle className="mt-1">{t.signing.subtitle}</PageSubtitle>
      </div>

      {/* Tabs and tools share one row: items-end keeps the strip's -mb-px
          underline on the header's bottom border, pb-2 lifts the tools off
          that border, and ml-auto keeps them right-aligned even after they
          wrap onto their own line at narrow widths. */}
      <div className="flex flex-wrap items-end gap-x-4">
        <UnderlineTabs
          className="-mb-px min-w-0"
          value={state.tab}
          onValueChange={(value) => state.setTab(value)}
          options={tabs}
          infoTarget="signing.tabs"
        />
        <div className="ml-auto flex min-w-0 flex-[1_1_auto] items-center justify-end gap-2 pb-2">
          <SigningFilterControl
            signers={state.signers}
            value={state.filter}
            onChange={state.setFilter}
          />
          <div className="relative w-56">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              {...pageInfoTarget("signing.search")}
              value={state.search}
              onChange={(e) => state.setSearch(e.target.value)}
              placeholder={t.signing.search}
              aria-label={t.signing.search}
              className={cn(CONTROL_FIELD_CLASS, "pl-8")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
